import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { Search, Plus, X, ChevronDown, Lock, Archive, RotateCcw } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { showStatusToast } from "../../components/ui/Toast";

// Paramètres KYC/LAB — onglet « Diligences » (catalogue de diligences, demande
// d'Anthony du 13/10/2026) : diligences prédéfinies et règles de
// correspondance. Les règles servent au calcul des suggestions du module
// KYC/LAB et de contexte à l'IA. Backend : controllers/GI/kycDiligencesController.js
// (mêmes droits et même historique de versions que les autres listes).

export const API_DILIGENCES = "https://backend-myalfa.vercel.app/api/gi/kyc-diligences";

const CHAMPS_FICHE = [
  ["objectif", "Risque / objectif"],
  ["travaux", "Travaux à réaliser"],
  ["pieces", "Pièces / preuve attendue"],
  ["signaux", "Signaux d'alerte"],
  ["etendue", "Étendue proposée"],
  ["frequence", "Moment / fréquence"],
  ["conclusion", "Conclusion attendue"],
  ["suites", "Revue / suites"],
  ["referenceRisque", "Référence risque"],
  ["nature", "Nature / statut"],
];
const EFFET_STYLE = {
  Socle: "bg-slate-100 text-slate-600",
  Recommandation: "bg-sky-50 text-sky-700",
  "Revue ciblée": "bg-amber-50 text-amber-700",
  "Examen ciblé": "bg-amber-50 text-amber-700",
  Précision: "bg-violet-50 text-violet-700",
};
const effetStyle = (e) => EFFET_STYLE[e] || "bg-rose-50 text-rose-700"; // obstacles, gel, escalade…
const EXEMPLE_CONDITION = '{"field":"C8","op":"eq","value":"Oui"}';

// Condition lisible : « C8 = Oui », « TAGS contient BTP », « (A ET B) »…
function conditionLisible(c) {
  if (!c || typeof c !== "object") return "—";
  if (Array.isArray(c.all)) return c.all.map((x) => (x.any ? `(${conditionLisible(x)})` : conditionLisible(x))).join(" ET ");
  if (Array.isArray(c.any)) return c.any.map((x) => (x.all ? `(${conditionLisible(x)})` : conditionLisible(x))).join(" OU ");
  const v = (x) => (x === true ? "oui" : x === false ? "non" : String(x));
  if (c.op === "eq") return `${c.field} = ${v(c.value)}`;
  if (c.op === "in") return `${c.field} parmi {${(c.value || []).join(", ")}}`;
  if (c.op === "contains") return `${c.field} contient ${v(c.value)}`;
  if (c.op === "intersects") return `${c.field} comprend l'une de {${(c.value || []).join(", ")}}`;
  return JSON.stringify(c);
}

// Champ texte enregistré à la sortie (Entrée = valider si mono-ligne, Échap = annuler).
function Texte({ value, disabled, placeholder, onCommit, multiligne = true, className = "" }) {
  const [v, setV] = useState(value || "");
  const ref = useRef(null);
  useEffect(() => setV(value || ""), [value]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [v]);
  if (disabled) return <p className={`text-xs text-slate-600 whitespace-pre-line m-0 px-1.5 py-1 ${className}`}>{value || "—"}</p>;
  return (
    <textarea
      ref={ref}
      rows={1}
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v.trim() !== (value || "") && onCommit(v.trim())}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (!multiligne || !e.shiftKey)) {
          e.preventDefault();
          e.target.blur();
        }
        if (e.key === "Escape") {
          setV(value || "");
          e.target.blur();
        }
      }}
      className={`w-full resize-none overflow-hidden bg-transparent border border-transparent hover:border-slate-200 focus:border-[#840040]/40 focus:bg-white rounded px-1.5 py-1 text-xs text-slate-700 focus:outline-none ${className}`}
    />
  );
}

const CodeChip = ({ code, libelle, onRemove }) => (
  <span title={libelle || code} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
    {code}
    {onRemove && (
      <button onClick={onRemove} className="text-slate-400 hover:text-rose-600" aria-label={`Retirer ${code}`}>
        <X className="w-2.5 h-2.5" />
      </button>
    )}
  </span>
);

const BTN = "flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm border border-[#840040] text-[#840040] hover:bg-[#840040] hover:text-white transition-colors";
const INPUT = "h-9 px-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#840040]/20";

/* ---------- Diligences ---------- */

function FicheDiligence({ d, peutModifier, accesConfidentiel, regles, modifier }) {
  const liees = regles.filter((r) => r.diligences.includes(d.code));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 px-4 py-3 bg-slate-50/70 border-t border-slate-100">
      {CHAMPS_FICHE.map(([k, l]) => (
        <div key={k}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 m-0 px-1.5">{l}</p>
          <Texte value={d[k]} placeholder="—" disabled={!peutModifier} onCommit={(v) => modifier(d, { [k]: v || null })} />
        </div>
      ))}
      <div className="md:col-span-2 flex flex-wrap items-center gap-2 pt-1">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 px-1.5">Règles qui la recommandent</span>
        {liees.length ? liees.map((r) => <CodeChip key={r.id} code={r.code} libelle={`${r.motif} — ${conditionLisible(r.condition)}`} />) : <span className="text-xs text-slate-400">Aucune (suggestion par l'IA ou ajout manuel uniquement)</span>}
      </div>
      {peutModifier && (
        <div className="md:col-span-2 flex flex-wrap items-center gap-2 pt-1">
          {accesConfidentiel && (
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={!!d.confidentiel} onChange={(e) => modifier(d, { confidentiel: e.target.checked })} />
              Accès restreint (Administrateurs et Experts-comptables)
            </label>
          )}
          <button
            onClick={() => modifier(d, { etat: d.etat === "archive" ? "actif" : "archive" })}
            className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs border border-slate-300 hover:bg-white"
          >
            {d.etat === "archive" ? <><RotateCcw className="w-3.5 h-3.5" /> Réactiver</> : <><Archive className="w-3.5 h-3.5" /> Archiver</>}
          </button>
        </div>
      )}
    </div>
  );
}

function Diligences({ cat, peutModifier, modifier, creer }) {
  const [recherche, setRecherche] = useState("");
  const [famille, setFamille] = useState("");
  const [archives, setArchives] = useState(false);
  const [ouverte, setOuverte] = useState(null);
  const [ajout, setAjout] = useState(null);
  const familles = useMemo(() => [...new Set(cat.diligences.map((d) => d.famille).filter(Boolean))], [cat.diligences]);
  const nbRegles = useMemo(() => {
    const n = {};
    cat.regles.filter((r) => r.etat === "actif").forEach((r) => r.diligences.forEach((c) => (n[c] = (n[c] || 0) + 1)));
    return n;
  }, [cat.regles]);
  const q = recherche.trim().toLowerCase();
  const lignes = cat.diligences.filter(
    (d) =>
      (archives ? d.etat === "archive" : d.etat !== "archive") &&
      (!famille || d.famille === famille) &&
      (!q || `${d.code} ${d.libelle} ${d.objectif || ""} ${d.signaux || ""}`.toLowerCase().includes(q))
  );
  const nbArchives = cat.diligences.filter((d) => d.etat === "archive").length;

  return (
    <>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Code, diligence, objectif, signal…" className={`${INPUT} w-72 pl-8`} aria-label="Rechercher une diligence" />
        </div>
        <select value={famille} onChange={(e) => setFamille(e.target.value)} className={`${INPUT} bg-white text-slate-700`} aria-label="Famille">
          <option value="">Toutes familles</option>
          {familles.map((f) => <option key={f}>{f}</option>)}
        </select>
        {nbArchives > 0 && (
          <button onClick={() => setArchives((a) => !a)} className={`flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm border ${archives ? "border-[#840040] text-[#840040]" : "border-slate-200 text-slate-600"}`}>
            <Archive className="w-4 h-4" /> {archives ? "Retour au catalogue" : `Archivées (${nbArchives})`}
          </button>
        )}
        {peutModifier && !archives && (
          <button onClick={() => setAjout({ libelle: "", famille: "", code: "" })} className={BTN}>
            <Plus className="w-4 h-4" /> Ajouter une diligence
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">{lignes.length} diligence{lignes.length > 1 ? "s" : ""}</span>
      </div>

      {ajout && (
        <div className="flex items-center gap-2 mb-3 p-3 rounded-xl border border-[#840040]/30 bg-[#840040]/5 flex-wrap">
          <input value={ajout.code} onChange={(e) => setAjout({ ...ajout, code: e.target.value.toUpperCase() })} placeholder="Code (auto)" className={`${INPUT} w-32`} aria-label="Code" />
          <input autoFocus value={ajout.libelle} onChange={(e) => setAjout({ ...ajout, libelle: e.target.value })} placeholder="Intitulé de la diligence" className={`${INPUT} flex-1 min-w-[240px]`} aria-label="Intitulé" />
          <input list="kyc-familles" value={ajout.famille} onChange={(e) => setAjout({ ...ajout, famille: e.target.value })} placeholder="Famille" className={`${INPUT} w-44`} aria-label="Famille" />
          <datalist id="kyc-familles">{familles.map((f) => <option key={f} value={f} />)}</datalist>
          <button
            disabled={!ajout.libelle.trim()}
            onClick={async () => {
              const d = await creer({ libelle: ajout.libelle, famille: ajout.famille || null, ...(ajout.code.trim() ? { code: ajout.code.trim() } : {}) });
              if (d) {
                setAjout(null);
                setOuverte(d.id);
              }
            }}
            className="h-9 px-3 rounded-lg text-sm font-semibold bg-[#840040] text-white hover:bg-[#6a0033] disabled:opacity-40"
          >
            Créer
          </button>
          <button onClick={() => setAjout(null)} className="h-9 px-3 rounded-lg text-sm border border-slate-300 hover:bg-white">Annuler</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm table-fixed min-w-[760px]">
          <colgroup>
            <col style={{ width: 96 }} />
            <col />
            <col style={{ width: 130 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 70 }} />
            <col style={{ width: 36 }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 text-left">
              <th className="px-3 py-2 font-semibold">Code</th>
              <th className="px-3 py-2 font-semibold">Diligence</th>
              <th className="px-3 py-2 font-semibold">Famille</th>
              <th className="px-3 py-2 font-semibold">Moment / fréquence</th>
              <th className="px-3 py-2 font-semibold text-center">Règles</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lignes.map((d) => {
              const ouv = ouverte === d.id;
              return (
                <React.Fragment key={d.id}>
                  <tr className={`align-top ${ouv ? "bg-slate-50/70" : "hover:bg-slate-50/50"}`}>
                    <td className="px-3 py-1.5 text-xs font-semibold text-slate-700">
                      <div className="flex items-center gap-1">
                        {d.code}
                        {d.confidentiel && <span title="Accès restreint"><Lock className="w-3 h-3 text-[#840040]" /></span>}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <Texte value={d.libelle} disabled={!peutModifier} multiligne={false} onCommit={(v) => v && modifier(d, { libelle: v })} className="font-medium text-slate-800" />
                      {!ouv && d.objectif && <p className="text-[10px] text-slate-400 m-0 px-1.5 line-clamp-1">{d.objectif}</p>}
                    </td>
                    <td className="px-2 py-1.5"><Texte value={d.famille} placeholder="—" disabled={!peutModifier} multiligne={false} onCommit={(v) => modifier(d, { famille: v || null })} /></td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{d.frequence || "—"}</td>
                    <td className="px-3 py-1.5 text-xs text-center text-slate-600">{nbRegles[d.code] || <span className="text-slate-300">0</span>}</td>
                    <td className="px-1 py-1.5 text-center">
                      <button onClick={() => setOuverte(ouv ? null : d.id)} className="p-1 rounded text-slate-400 hover:text-[#840040] hover:bg-slate-100" title={ouv ? "Fermer la fiche" : "Ouvrir la fiche"} aria-expanded={ouv} aria-label={ouv ? "Fermer la fiche" : "Ouvrir la fiche"}>
                        <ChevronDown className={`w-4 h-4 transition-transform ${ouv ? "rotate-180" : ""}`} />
                      </button>
                    </td>
                  </tr>
                  {ouv && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <FicheDiligence d={d} peutModifier={peutModifier} accesConfidentiel={cat.accesConfidentiel} regles={cat.regles.filter((r) => r.etat === "actif")} modifier={modifier} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {lignes.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400">Aucune diligence ne correspond à la recherche.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------- Règles ---------- */

// Édition d'une règle (création ou modification) : la condition se saisit en
// JSON, avec sa lecture en clair dessous.
function EditionRegle({ initiale, cat, onAnnuler, onEnregistrer }) {
  const [r, setR] = useState(() => ({ ...initiale, conditionTexte: initiale.condition ? JSON.stringify(initiale.condition) : EXEMPLE_CONDITION }));
  const [busy, setBusy] = useState(false);
  let condition = null;
  let erreurJson = null;
  try {
    condition = JSON.parse(r.conditionTexte);
  } catch {
    erreurJson = "JSON invalide";
  }
  const parCode = Object.fromEntries(cat.diligences.map((d) => [d.code, d]));
  const disponibles = cat.diligences.filter((d) => d.etat !== "archive" && !r.diligences.includes(d.code));
  const champsConnus = cat.champs || [];
  return (
    <div className="px-4 py-3 bg-slate-50/70 border-t border-slate-100 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_200px] gap-3">
        <label className="text-xs text-slate-500">Motif de déclenchement
          <input value={r.motif || ""} onChange={(e) => setR({ ...r, motif: e.target.value })} className={`${INPUT} w-full mt-1`} />
        </label>
        <label className="text-xs text-slate-500">Priorité
          <input type="number" min={0} max={99} value={r.priorite ?? 30} onChange={(e) => setR({ ...r, priorite: e.target.value })} className={`${INPUT} w-full mt-1`} title="Plus petit = plus prioritaire" />
        </label>
        <label className="text-xs text-slate-500">Effet
          <input list="kyc-effets" value={r.effet || ""} onChange={(e) => setR({ ...r, effet: e.target.value })} className={`${INPUT} w-full mt-1`} />
          <datalist id="kyc-effets">{[...new Set(cat.regles.map((x) => x.effet).filter(Boolean))].map((x) => <option key={x} value={x} />)}</datalist>
        </label>
      </div>
      <div>
        <p className="text-xs text-slate-500 m-0 mb-1">Condition (JSON : field / op eq, in, contains, intersects ; combinaisons all / any)</p>
        <textarea value={r.conditionTexte} onChange={(e) => setR({ ...r, conditionTexte: e.target.value })} rows={2} spellCheck={false} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#840040]/20" />
        <p className={`text-xs m-0 mt-1 ${erreurJson ? "text-rose-600" : "text-slate-600"}`}>{erreurJson || <>Lecture : <b>{conditionLisible(condition)}</b></>}</p>
        <details className="mt-1">
          <summary className="text-[11px] text-[#840040] cursor-pointer">Champs disponibles ({champsConnus.length})</summary>
          <div className="mt-1 max-h-40 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-x-4 text-[11px] text-slate-600">
            {champsConnus.map((c) => <span key={c.code}><b>{c.code}</b> — {c.libelle} <span className="text-slate-400">({c.type})</span></span>)}
          </div>
        </details>
      </div>
      <div>
        <p className="text-xs text-slate-500 m-0 mb-1">Diligences recommandées</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {r.diligences.map((c) => <CodeChip key={c} code={c} libelle={parCode[c]?.libelle} onRemove={() => setR({ ...r, diligences: r.diligences.filter((x) => x !== c) })} />)}
          <select value="" onChange={(e) => e.target.value && setR({ ...r, diligences: [...r.diligences, e.target.value] })} className="h-7 px-1.5 rounded-md border border-slate-200 text-xs bg-white" aria-label="Ajouter une diligence ciblée">
            <option value="">+ Ajouter…</option>
            {disponibles.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.libelle}</option>)}
          </select>
        </div>
      </div>
      <label className="block text-xs text-slate-500">Justification
        <input value={r.justification || ""} onChange={(e) => setR({ ...r, justification: e.target.value })} className={`${INPUT} w-full mt-1`} />
      </label>
      <div className="flex items-center gap-2">
        {initiale.id && (
          <button
            onClick={async () => {
              setBusy(true);
              await onEnregistrer({ etat: initiale.etat === "archive" ? "actif" : "archive" });
              setBusy(false);
            }}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs border border-slate-300 hover:bg-white"
            disabled={busy}
          >
            {initiale.etat === "archive" ? <><RotateCcw className="w-3.5 h-3.5" /> Réactiver</> : <><Archive className="w-3.5 h-3.5" /> Archiver</>}
          </button>
        )}
        <span className="flex-1" />
        <button onClick={onAnnuler} className="h-8 px-3 rounded-lg text-xs border border-slate-300 hover:bg-white" disabled={busy}>Annuler</button>
        <button
          disabled={busy || !!erreurJson || !r.motif?.trim() || !r.diligences.length}
          onClick={async () => {
            setBusy(true);
            await onEnregistrer({ motif: r.motif, condition, diligences: r.diligences, priorite: Number(r.priorite), effet: r.effet || null, justification: r.justification || null });
            setBusy(false);
          }}
          className="h-8 px-3 rounded-lg text-xs font-semibold bg-[#840040] text-white hover:bg-[#6a0033] disabled:opacity-40"
        >
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

function Regles({ cat, peutModifier, enregistrerRegle }) {
  const [recherche, setRecherche] = useState("");
  const [effet, setEffet] = useState("");
  const [archives, setArchives] = useState(false);
  const [edition, setEdition] = useState(null); // id | "nouvelle"
  const parCode = useMemo(() => Object.fromEntries(cat.diligences.map((d) => [d.code, d])), [cat.diligences]);
  const effets = useMemo(() => [...new Set(cat.regles.map((r) => r.effet).filter(Boolean))], [cat.regles]);
  const q = recherche.trim().toLowerCase();
  const lignes = cat.regles.filter(
    (r) =>
      (archives ? r.etat === "archive" : r.etat !== "archive") &&
      (!effet || r.effet === effet) &&
      (!q || `${r.code} ${r.motif} ${conditionLisible(r.condition)} ${r.diligences.join(" ")}`.toLowerCase().includes(q))
  );
  const nbArchives = cat.regles.filter((r) => r.etat === "archive").length;

  return (
    <>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Code, motif, champ, diligence…" className={`${INPUT} w-72 pl-8`} aria-label="Rechercher une règle" />
        </div>
        <select value={effet} onChange={(e) => setEffet(e.target.value)} className={`${INPUT} bg-white text-slate-700`} aria-label="Effet">
          <option value="">Tous effets</option>
          {effets.map((x) => <option key={x}>{x}</option>)}
        </select>
        {nbArchives > 0 && (
          <button onClick={() => setArchives((a) => !a)} className={`flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm border ${archives ? "border-[#840040] text-[#840040]" : "border-slate-200 text-slate-600"}`}>
            <Archive className="w-4 h-4" /> {archives ? "Retour aux règles" : `Archivées (${nbArchives})`}
          </button>
        )}
        {peutModifier && !archives && (
          <button onClick={() => setEdition("nouvelle")} className={BTN}>
            <Plus className="w-4 h-4" /> Ajouter une règle
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">{lignes.length} règle{lignes.length > 1 ? "s" : ""}</span>
      </div>

      {edition === "nouvelle" && (
        <div className="mb-3 rounded-xl border border-[#840040]/30 overflow-hidden">
          <EditionRegle
            initiale={{ motif: "", diligences: [], priorite: 30, effet: "Recommandation" }}
            cat={cat}
            onAnnuler={() => setEdition(null)}
            onEnregistrer={async (corps) => {
              if (await enregistrerRegle(null, corps)) setEdition(null);
            }}
          />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm table-fixed min-w-[860px]">
          <colgroup>
            <col style={{ width: 130 }} />
            <col style={{ width: "24%" }} />
            <col />
            <col style={{ width: "22%" }} />
            <col style={{ width: 64 }} />
            <col style={{ width: 130 }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 text-left">
              <th className="px-3 py-2 font-semibold">Code</th>
              <th className="px-3 py-2 font-semibold">Motif</th>
              <th className="px-3 py-2 font-semibold">Condition</th>
              <th className="px-3 py-2 font-semibold">Diligences</th>
              <th className="px-3 py-2 font-semibold text-center">Priorité</th>
              <th className="px-3 py-2 font-semibold">Effet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lignes.map((r) => {
              const ouv = edition === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className={`align-top ${peutModifier ? "cursor-pointer" : ""} ${ouv ? "bg-slate-50/70" : "hover:bg-slate-50/50"}`}
                    onClick={() => peutModifier && setEdition(ouv ? null : r.id)}
                  >
                    <td className="px-3 py-2 text-xs font-semibold text-slate-700">{r.code}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{r.motif}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 font-mono break-words">{conditionLisible(r.condition)}</td>
                    <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{r.diligences.map((c) => <CodeChip key={c} code={c} libelle={parCode[c]?.libelle} />)}</div></td>
                    <td className="px-3 py-2 text-xs text-center text-slate-600">{r.priorite}</td>
                    <td className="px-3 py-2"><span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${effetStyle(r.effet)}`}>{r.effet || "—"}</span></td>
                  </tr>
                  {ouv && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <EditionRegle
                          initiale={r}
                          cat={cat}
                          onAnnuler={() => setEdition(null)}
                          onEnregistrer={async (corps) => {
                            if (await enregistrerRegle(r, corps)) setEdition(null);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {lignes.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400">Aucune règle ne correspond à la recherche.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------- Onglet ---------- */

// cat = réponse de GET /gi/kyc-diligences (chargée par ParametresKycLab).
export default function ParametresKycDiligences({ cat, setCat, onVersion }) {
  const [vue, setVue] = useState("diligences");
  const peutModifier = !!cat?.peutModifier;

  const remplacer = (cle, objet) => setCat((c) => ({ ...c, [cle]: c[cle].some((x) => x.id === objet.id) ? c[cle].map((x) => (x.id === objet.id ? objet : x)) : [...c[cle], objet] }));
  const erreurApi = (err) => (err?.response?.status === 403 ? "Modification réservée aux personnes habilitées." : err?.response?.data?.error || "La modification n'a pas pu être enregistrée.");

  // Optimiste, comme les autres listes : affichée tout de suite, version
  // publiée en arrière-plan, retour arrière en cas d'échec.
  const modifier = (d, patch) => {
    if (Object.keys(patch).every((k) => patch[k] === d[k])) return;
    remplacer("diligences", { ...d, ...patch });
    const toast = showStatusToast({ status: "loading", titre: "Enregistrement...", message: `${d.code} — publication d'une nouvelle version.` });
    httpClient
      .patch(`${API_DILIGENCES}/${encodeURIComponent(d.id)}`, patch)
      .then(({ data: res }) => {
        remplacer("diligences", res.diligence);
        if (res.version) {
          onVersion(res.version);
          toast.update({ status: "success", titre: `Version ${res.version.version} publiée`, message: `${d.code} modifiée.` });
        } else toast.update({ status: "success", titre: "Aucune modification", message: "" });
      })
      .catch((err) => {
        remplacer("diligences", d);
        toast.update({ status: "error", titre: "Erreur", message: erreurApi(err) });
      });
  };

  const creer = async (corps) => {
    const toast = showStatusToast({ status: "loading", titre: "Création...", message: corps.libelle });
    try {
      const { data: res } = await httpClient.post(API_DILIGENCES, corps);
      remplacer("diligences", res.diligence);
      if (res.version) onVersion(res.version);
      toast.update({ status: "success", titre: `${res.diligence.code} ajoutée`, message: res.diligence.libelle });
      return res.diligence;
    } catch (err) {
      toast.update({ status: "error", titre: "Erreur", message: erreurApi(err) });
      return null;
    }
  };

  const enregistrerRegle = async (regle, corps) => {
    const toast = showStatusToast({ status: "loading", titre: "Enregistrement...", message: regle ? `Règle ${regle.code}` : "Nouvelle règle" });
    try {
      const { data: res } = regle
        ? await httpClient.patch(`${API_DILIGENCES}/regles/${encodeURIComponent(regle.id)}`, corps)
        : await httpClient.post(`${API_DILIGENCES}/regles`, corps);
      remplacer("regles", res.regle);
      if (res.version) onVersion(res.version);
      toast.update({ status: "success", titre: res.version ? `Version ${res.version.version} publiée` : "Aucune modification", message: `Règle ${res.regle.code}` });
      return true;
    } catch (err) {
      toast.update({ status: "error", titre: "Erreur", message: erreurApi(err) });
      return false;
    }
  };

  if (!cat) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-6 rounded bg-slate-100 animate-pulse" />)}
      </div>
    );
  }
  if (cat.erreur) return <p className="text-sm text-rose-600">{cat.erreur}</p>;

  return (
    <>
      <p className="text-xs text-slate-500 mt-0 mb-3 max-w-3xl">
        Diligences proposées dans le module KYC/LAB de chaque dossier. Les règles calculent les recommandations à partir des données du dossier
        (questions, forme, missions, risques, tags) et servent de contexte à l'IA pour ses suggestions.
      </p>
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 mb-3" role="group" aria-label="Vue du catalogue">
        {[["diligences", `Diligences (${cat.diligences.filter((d) => d.etat !== "archive").length})`], ["regles", `Règles (${cat.regles.filter((r) => r.etat !== "archive").length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setVue(k)} aria-pressed={vue === k} className={`h-8 px-3 rounded-md text-sm ${vue === k ? "bg-[#840040] text-white font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {vue === "diligences" ? (
        <Diligences cat={cat} peutModifier={peutModifier} modifier={modifier} creer={creer} />
      ) : (
        <Regles cat={cat} peutModifier={peutModifier} enregistrerRegle={enregistrerRegle} />
      )}
    </>
  );
}
